/**
 * Pure functions for DataTable filter + sort logic (iter276 extraction).
 * Kept in a separate file so the orchestrator component stays focused on
 * rendering + state, and these helpers can be reused/unit-tested.
 */

export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.+Z-]+)?$/;

/**
 * Filter rows by global searchTerm + per-column filter map. Mirrors the
 * pre-refactor behavior byte-for-byte (status-field default → 'active',
 * select filter exact match, text filter contains match).
 */
export function applyFilters({ data, columns, searchTerm, searchFields, filters }) {
  let result = data;

  if (searchTerm && searchFields.length > 0) {
    const term = searchTerm.toLowerCase();
    result = result.filter(item =>
      searchFields.some(field => {
        const value = getNestedValue(item, field);
        return value && String(value).toLowerCase().includes(term);
      })
    );
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value && value !== 'all') {
      const column = columns.find(col => col.key === key);
      result = result.filter(item => {
        let itemValue = getNestedValue(item, key);
        // Special handling for status field: undefined/null defaults to 'active'
        if (key === 'status' && (itemValue === undefined || itemValue === null)) {
          itemValue = 'active';
        }
        // iter318 wave 24u — `is_active` is a boolean but production
        // records commonly have it unset. The Status column renders
        // "Active" for anything !== false, so the filter MUST mirror
        // that: undefined/null → true. Otherwise 68 of 217 parents
        // display as "Active" yet get dropped when Active is picked.
        if (key === 'is_active' && (itemValue === undefined || itemValue === null)) {
          itemValue = true;
        }
        if (column?.filterType === 'select') {
          return String(itemValue).toLowerCase() === String(value).toLowerCase();
        }
        return String(itemValue || '').toLowerCase().includes(value.toLowerCase());
      });
    }
  });

  return result;
}

/**
 * Sort rows by a sortConfig {key, direction}. Returns a new array;
 * caller should pass a mutable copy if performance matters.
 * Handles ISO dates, numeric strings, plain strings, and numbers.
 */
export function applySort(data, sortConfig) {
  if (!sortConfig?.key) return data;
  const { key, direction } = sortConfig;
  const sorted = [...data];
  sorted.sort((a, b) => {
    const aVal = getNestedValue(a, key);
    const bVal = getNestedValue(b, key);

    // Push nullish values to the end regardless of direction
    if (aVal === null || aVal === undefined || aVal === '') return 1;
    if (bVal === null || bVal === undefined || bVal === '') return -1;

    // ISO date / datetime detection — chronological sort for raw string dates.
    const aIsDate = typeof aVal === 'string' && ISO_DATE_RE.test(aVal);
    const bIsDate = typeof bVal === 'string' && ISO_DATE_RE.test(bVal);
    if (aIsDate && bIsDate) {
      const aTime = new Date(aVal).getTime();
      const bTime = new Date(bVal).getTime();
      return direction === 'asc' ? aTime - bTime : bTime - aTime;
    }

    // Numeric-string handling (e.g., cricclubs_id)
    const aNum = typeof aVal === 'string' ? parseFloat(aVal) : aVal;
    const bNum = typeof bVal === 'string' ? parseFloat(bVal) : bVal;
    const aIsNumeric = typeof aVal === 'string' && !isNaN(aNum) && isFinite(aNum);
    const bIsNumeric = typeof bVal === 'string' && !isNaN(bNum) && isFinite(bNum);
    if (aIsNumeric && bIsNumeric) {
      return direction === 'asc' ? aNum - bNum : bNum - aNum;
    }

    if (typeof aVal === 'string') {
      return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    return direction === 'asc' ? aVal - bVal : bVal - aVal;
  });
  return sorted;
}
